/**
 * Gallery Component
 * Carousel showing one photo at a time with left/right navigation
 * Click image to open full-page lightbox
 * Supports keyboard navigation and touch gestures
 */

import React, { useState, useEffect, useRef } from 'react';

const Gallery = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [fadeTransition, setFadeTransition] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goToPrevious = () => {
    setFadeTransition(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      setFadeTransition(false);
    }, 150);
  };

  const goToNext = () => {
    setFadeTransition(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      setFadeTransition(false);
    }, 150);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') setShowLightbox(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox]);

  // Early return AFTER all hooks
  if (!images || images.length === 0) return null;

  // Touch gestures for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      // Swipe left -> next
      goToNext();
    }
    if (touchStartX.current - touchEndX.current < -50) {
      // Swipe right -> previous
      goToPrevious();
    }
  };

  return (
    <>
      {/* Carousel */}
      <div 
        style={{ 
          position: 'relative', 
          width: '100%', 
          aspectRatio: '4/3', 
          background: 'var(--smoke-gray)', 
          borderRadius: '8px', 
          overflow: 'hidden', 
          boxShadow: 'var(--shadow-card)' 
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Main image with fade transition */}
        <img 
          src={images[currentIndex]} 
          alt={`Gallery ${currentIndex + 1}`}
          onClick={() => setShowLightbox(true)}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            cursor: 'pointer',
            opacity: fadeTransition ? 0.3 : 1,
            transition: 'opacity 0.15s ease'
          }}
        />
        
        {/* Left arrow */}
        {images.length > 1 && (
          <button 
            onClick={goToPrevious} 
            aria-label="Previous image"
            style={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              background: 'rgba(26, 26, 26, 0.7)', 
              border: 'none', 
              color: 'var(--text-light)', 
              fontSize: '24px', 
              width: '48px', 
              height: '48px', 
              borderRadius: '50%', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              transition: 'background 0.2s',
              zIndex: 10
            }} 
            onMouseEnter={(e) => e.target.style.background = 'rgba(26, 26, 26, 0.9)'} 
            onMouseLeave={(e) => e.target.style.background = 'rgba(26, 26, 26, 0.7)'}
          >
            ‹
          </button>
        )}
        
        {/* Right arrow */}
        {images.length > 1 && (
          <button 
            onClick={goToNext} 
            aria-label="Next image"
            style={{ 
              position: 'absolute', 
              right: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              background: 'rgba(26, 26, 26, 0.7)', 
              border: 'none', 
              color: 'var(--text-light)', 
              fontSize: '24px', 
              width: '48px', 
              height: '48px', 
              borderRadius: '50%', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              transition: 'background 0.2s',
              zIndex: 10
            }} 
            onMouseEnter={(e) => e.target.style.background = 'rgba(26, 26, 26, 0.9)'} 
            onMouseLeave={(e) => e.target.style.background = 'rgba(26, 26, 26, 0.7)'}
          >
            ›
          </button>
        )}
        
        {/* Image counter - bottom center */}
        <div style={{ 
          position: 'absolute', 
          bottom: '16px', 
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(26, 26, 26, 0.8)', 
          color: 'var(--text-light)', 
          padding: '6px 12px', 
          borderRadius: '20px', 
          fontSize: '13px', 
          fontWeight: '600',
          zIndex: 10
        }}>
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div 
          onClick={() => setShowLightbox(false)} 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.95)', 
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            cursor: 'pointer',
            padding: '20px'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img 
            src={images[currentIndex]} 
            alt={`Full size ${currentIndex + 1}`} 
            style={{ 
              maxWidth: '90%', 
              maxHeight: '90%', 
              objectFit: 'contain',
              opacity: fadeTransition ? 0.3 : 1,
              transition: 'opacity 0.15s ease'
            }} 
            onClick={(e) => e.stopPropagation()} 
          />
          
          {/* Close button */}
          <button 
            onClick={() => setShowLightbox(false)} 
            aria-label="Close lightbox"
            style={{ 
              position: 'absolute', 
              top: '20px', 
              right: '20px', 
              background: 'rgba(255, 255, 255, 0.2)', 
              border: 'none', 
              color: 'white', 
              fontSize: '32px', 
              width: '48px', 
              height: '48px', 
              borderRadius: '50%', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'} 
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
          
          {/* Navigation in lightbox */}
          {images.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }} 
                aria-label="Previous image"
                style={{ 
                  position: 'absolute', 
                  left: '20px', 
                  background: 'rgba(255, 255, 255, 0.2)', 
                  border: 'none', 
                  color: 'white', 
                  fontSize: '48px', 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'} 
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ‹
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); goToNext(); }} 
                aria-label="Next image"
                style={{ 
                  position: 'absolute', 
                  right: '20px', 
                  background: 'rgba(255, 255, 255, 0.2)', 
                  border: 'none', 
                  color: 'white', 
                  fontSize: '48px', 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'} 
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ›
              </button>
              
              {/* Counter in lightbox */}
              <div style={{ 
                position: 'absolute', 
                bottom: '30px', 
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(255, 255, 255, 0.2)', 
                color: 'white', 
                padding: '8px 16px', 
                borderRadius: '20px', 
                fontSize: '14px', 
                fontWeight: '600'
              }}>
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Gallery;
